/**
 * 튜닝 사례 등록·편집 — **화면이 절차를 쥔다.**
 *
 * 예전에는 대화로만 등록했다. 흐름은 정해져 있는데(첨부 → 분석 → 양식 → 검토 → 저장)
 * 채팅으로 몰면 사용자가 매번 말로 지시해야 하고, 모델이 단계를 건너뛰거나 순서를 바꿔도
 * 막을 방법이 없었다. 이제 LLM 은 **「첨부에서 양식을 채운다」 한 가지 일**만 한다.
 *
 * ## 양식이 곧 저장 구조다
 *
 * 여기 칸들은 `asst_tune_case` 의 컬럼과 1:1 이다. 화면에서만 쓰는 필드를 만들지 않는다 —
 * 만들면 저장할 때 어디로 갈지 정해야 하고, 그 판단이 두 곳으로 갈린다.
 *
 * ## 초안은 시작점이지 결과가 아니다
 *
 * 모델이 채운 뒤 **사용자가 직접 고칠 수 있어야** 한다. 말로 고치고 싶으면 「AI 에게 다시
 * 쓰기」에 지시를 주면 지금 양식을 통째로 다시 받는다. 어느 쪽이든 저장 전에 사람이 본다.
 *
 * ## 변조 매핑은 화면에서도 고칠 수 있어야 한다
 *
 * 범용(공개) 사례는 서버가 매핑으로 기계 검증하고, 원본 이름이 하나라도 남아 있으면 저장을
 * 거부한다(422). 매핑을 모델만 만들 수 있게 두면 손으로 쓴 사례는 **영원히 저장할 수 없다** —
 * 그래서 매핑 표를 직접 편집한다. 거부되면 남은 식별자를 그대로 보여 준다.
 *
 * ## 「변조하지 않음」도 고를 수 있어야 한다
 *
 * 공개한다고 늘 변조해야 하는 것은 아니다. **이미 변조된 자료**이거나 시스템 고유 이름이
 * 애초에 없는 자료가 실제로 있고, 그때 매핑을 강요하면 등록 자체가 막힌다. 이 선언은
 * 사람만 할 수 있다(LLM 은 못 한다) — 기계가 확인할 수 없는 주장이기 때문이다. 대신 그렇게
 * 고르면 초안 단계에서도 **이름을 바꾸지 말라고** 모델에 시킨다. 이미 변조된 자료를 한 번 더
 * 바꾸면 원본과의 대응이 끊겨 사용자가 검토할 수 없다.
 */
import TextFormatBar from './TextFormatBar';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AgentStrip from './AgentStrip';
import useMonitoringAccess from '../../hooks/useMonitoringAccess';
import { IS_STANDALONE } from '../../config/features';
import { getDefaults as getExperienceDefaults } from '../../services/experience';
import {
    FiPaperclip, FiX, FiCpu, FiSave, FiAlertCircle, FiPlus, FiLoader, FiUser, FiLink, FiCheckCircle, FiCopy,
} from 'react-icons/fi';
import {
    uploadTuningSources, uploadCaseFigures, deleteTuningSource, getSourceSlides, getSourceSlide, slideThumbUrl, buildCaseDraft, validateCaseDraft, saveTuningCase, getCaseFolders,
    getAccount, getCaseTopics, addTuningSourceUrl,
} from '../../services/assistantClient';
import { describeAccount } from './accountBadge';
import ReportView from '../sqlTuning/ReportView';
import { caseDocument, caseRefs } from './caseDocument';
import CaseRedactPanel from './CaseRedactPanel';
import { usePrompt } from '../PromptDialog';
import { alignPipeTables } from '../../utils/planAlign';
import { USAGES, PERF_BASIS, RESULT_SAME, derivedUsage, figureUrl, MAX_FIGURES, FIGURE_BGS, FIGURE_WIDTH } from '../../utils/caseMeta';
import { svgToPngFile } from '../../utils/svgToPng';
import { topSources, childCount } from '../../utils/sourceTree';
import { pickDiagramSlides, AUTO_SLIDE_FIGURES } from '../../utils/slideFigures';

const ACCEPT = '.txt,.sql,.log,.md,.csv,.json,.png,.jpg,.jpeg,.gif,.webp,.pdf,.pptx';

/**
 * 양식 칸 — 순서가 화면 순서다. rows 가 있으면 여러 줄 입력.
 *
 * `tab` 으로 나눈 이유: 전/후 쿼리와 실행계획은 한 건이 수십 줄이라 한 화면에 같이 두면
 * 제목·본문 같은 짧은 칸이 스크롤 저 아래로 밀린다. **칸 목록은 하나로 둔다** — 탭마다
 * 배열을 따로 만들면 서버의 양식(draft.js FIELDS)과 맞추는 자리가 여러 곳이 된다.
 */
const FIELDS = [
    { key: 'title', tab: 'basic', rows: 0, required: true },
    { key: 'symptom', tab: 'basic', rows: 0, half: true },
    { key: 'improvement', tab: 'basic', rows: 0, half: true },
    { key: 'dbms', tab: 'basic', rows: 0, half: true },
    { key: 'dbmsVersion', tab: 'basic', rows: 0, half: true },
    { key: 'body', tab: 'basic', rows: 10, required: true },
    // **유의사항** — 개선 전 내용만 보고는 도달하기 어려운 판단. 대조(5단계)가 초안을 채워 준다.
    { key: 'caution', tab: 'basic', rows: 4 },
    { key: 'beforeSql', tab: 'sql', rows: 12, mono: true },
    { key: 'afterSql', tab: 'sql', rows: 12, mono: true },
    { key: 'indexDdl', tab: 'sql', rows: 4, mono: true },
    { key: 'beforePlan', tab: 'plan', rows: 14, mono: true },
    { key: 'afterPlan', tab: 'plan', rows: 14, mono: true },
];

// preview — 저장 전 미리보기(상세 화면과 같은 문서). 칸이 아니라 보기라 FIELDS 에는 없다(2026-10-11)
const TABS = ['basic', 'sql', 'plan', 'figures', 'preview'];

/**
 * 등록 워크플로 단계(사용자 요청 2026-10-11 — SQL 튜닝 화면처럼 리본으로). 새 사례·편집·복제 후 편집이 **같은 화면**이다.
 * 변조(이름 바꾸기)와 대조는 선택 단계 — 건너뛰어도 저장된다. 단계는 자유롭게 오간다(앞 단계를 끝내야 열리는 잠금은 없다).
 */
const STEPS = ['attach', 'visibility', 'draft', 'edit', 'redact', 'check', 'save'];
const OPTIONAL_STEPS = ['draft', 'redact', 'check'];

/**
 * 폴더 고르기 — 있는 폴더 목록에서 고르고, 맨 아래 「새 폴더…」 로 새 이름을 적는다(2026-10-11).
 * 예전에는 입력칸 + datalist 였는데, 값이 차 있으면 목록이 그 값 하나로 걸러져 말풍선처럼만 보이고 고를 수가 없었다.
 * 새 폴더는 저장할 때 만들어진다(서버 saveCase). 2단계 폴더 칸과 복제본 폴더가 같은 것을 쓴다.
 */
function FolderPicker({ id, value, options, onChange, t }) {
    const prompt = usePrompt();
    const list = value && !options.includes(value) ? [...options, value].sort() : options;
    const pick = async (v) => {
        if (v !== '__new__') { onChange(v); return; }
        const name = await prompt({
            title: t('assistant:caseFolder.newTitle'),
            message: t('assistant:caseFolder.newHint'),
            placeholder: t('assistant:caseFolder.namePlaceholder'),
            confirmText: t('assistant:caseFolder.create'),
        });
        if (name && String(name).trim()) onChange(String(name).trim());
    };
    return (
        <select id={id} className="tcf-select tcf-folderInput" value={value || ''} onChange={(e) => pick(e.target.value)}>
            <option value="">{t('assistant:caseFolder.noneOption')}</option>
            {list.map(f => <option key={f} value={f}>{f}</option>)}
            <option value="__new__">{t('assistant:caseFolder.newOption')}</option>
        </select>
    );
}
FolderPicker.propTypes = {
    id: PropTypes.string, value: PropTypes.string, options: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChange: PropTypes.func.isRequired, t: PropTypes.func.isRequired,
};

/**
 * 개선안 대조 — 모델이 개선 전 내용만 보고 낸 개선 쿼리·인덱스를 이 사례의 개선 후와 **나란히** 둔다.
 * 판정하지 않는다: 같은 개선이 힌트·재작성·인덱스처럼 다른 모양으로 쓰일 수 있어 기계 비교는 거짓 불일치를 낸다.
 */
function ProposalCompare({ verdict, form, t }) {
    const rows = [
        { key: 'sql', label: t('assistant:caseForm.proposalSql'), model: verdict && verdict.proposedSql, mine: form.afterSql },
        { key: 'index', label: t('assistant:caseForm.proposalIndex'), model: verdict && verdict.proposedIndexDdl, mine: form.indexDdl },
    ].filter(r => (r.model && r.model.trim()) || (r.mine && r.mine.trim()));
    if (!rows.length) return null;
    const cell = (v) => (v && v.trim()
        ? <pre className="tcf-proposalPre">{v}</pre>
        : <span className="tcf-hint">{t('assistant:caseForm.proposalNone')}</span>);
    return (
        <>
            <div className="tcf-checkHead">{t('assistant:caseForm.checkProposal')}</div>
            <div className="tcf-proposal">
                <div className="tcf-proposalHead">{t('assistant:caseForm.proposalModel')}</div>
                <div className="tcf-proposalHead">{t('assistant:caseForm.proposalCase')}</div>
                {rows.map(r => (
                    <React.Fragment key={r.key}>
                        <div className="tcf-proposalLabel">{r.label}</div>
                        <div className="tcf-proposalCol">{cell(r.model)}</div>
                        <div className="tcf-proposalCol">{cell(r.mine)}</div>
                    </React.Fragment>
                ))}
            </div>
            <div className="tcf-checkLegend">{t('assistant:caseForm.proposalNote')}</div>
        </>
    );
}
const EMPTY = FIELDS.reduce((o, f) => ({ ...o, [f.key]: '' }), {});

/** 저장된 사례 → 양식. **모르는 필드는 버린다** (양식이 곧 저장 구조다). */
/**
 * 실행계획 칸 맞추기 — **편집 칸에도** 미리보기·상세와 같은 맞춤(utils/planAlign, 한글 2칸)을 적용한다(사용자 지적 2026-10-11:
 * 편집 칸은 삐뚤고 미리보기는 반듯하면 둘이 달라 고치기 어렵다). 불러올 때·AI 초안이 올 때·칸에서 나갈 때 맞추고, 그 모양으로 저장한다.
 * 맞춤은 칸 사이 공백만 바꾼다(값·순서·표가 아닌 줄은 그대로).
 */
const PLAN_KEYS = ['beforePlan', 'afterPlan'];
function alignPlans(o) {
    const out = { ...o };
    for (const k of PLAN_KEYS) if (out[k]) out[k] = alignPipeTables(out[k]);
    return out;
}

function toForm(c) {
    if (!c) return EMPTY;
    const o = { ...EMPTY };
    for (const f of FIELDS) o[f.key] = c[f.key] || '';
    return alignPlans(o);
}

/**
 * 실패 안내 — **사라지지 않는다.**
 *
 * 문구 한 줄로는 고칠 수가 없다. 모델이 실제로 뭐라고 했는지(raw), 어느 첨부를 못 읽었는지
 * (unusable), 어떤 원본 이름이 남았는지(leaked)를 서버가 주는 대로 펼쳐 둔다.
 */
function Problem({ error, onClose, t }) {
    if (!error) return null;
    const { message, raw, unusable, leaked } = error;
    return (
        <div className="tcf-leak">
            <FiAlertCircle size={12} />
            <div className="tcf-leakBody">
                <div>{message}</div>
                {leaked && leaked.length ? (
                    <>
                        <div className="tcf-leakSub">{t('assistant:caseForm.leakTitle')}</div>
                        <code>{leaked.map(l => l.original + (l.why ? ` (${l.why})` : '')).join(', ')}</code>
                    </>
                ) : null}
                {unusable && unusable.length ? (
                    <>
                        <div className="tcf-leakSub">{t('assistant:caseForm.unreadable')}</div>
                        <code>{unusable.map(u => (u.note ? `${u.filename} — ${u.note}` : u.filename)).join('\n')}</code>
                    </>
                ) : null}
                {raw ? (
                    <>
                        <div className="tcf-leakSub">{t('assistant:caseForm.modelSaid')}</div>
                        <code>{raw}</code>
                    </>
                ) : null}
            </div>
            <button type="button" className="tcf-iconBtn" onClick={onClose} title={t('common:ui.close')}>
                <FiX size={11} />
            </button>
        </div>
    );
}

Problem.propTypes = {
    // eslint-disable-next-line react/forbid-prop-types -- 서버가 준 근거를 그대로 담는다. 여기서 나열하면 서버가 늘릴 때마다 뒤처진다
    error: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
};

/** 매핑 객체 ↔ 편집용 행. */
const toRows = (map) => Object.entries(map || {}).map(([from, to]) => ({ from, to }));
const toMap = (rows) => rows.reduce((m, r) => {
    const k = String(r.from || '').trim();
    if (k && String(r.to || '').trim()) m[k] = String(r.to).trim();
    return m;
}, {});

function CaseRegisterForm({ initial, initialTab, preload, agentDraft, provider, onSaved, onCancel, onManageTopics, defaultFolder, onFoldersChanged }) {
    const { t } = useTranslation();
    const inputRef = useRef(null);

    /**
     * 어느 AI 로 초안을 만들 것인가. 패널이 쓰는 것을 기본으로 받되 **여기서 바꿀 수 있다** —
     * 사례 정리는 대화와 다른 일이라 다른 계정/모델을 쓰고 싶을 수 있고, 무엇보다 로그인된
     * 쪽이 어느 쪽인지 눌러 보기 전에 알아야 한다.
     */
    const [ai, setAi] = useState(provider || 'claude');
    const [account, setAccount] = useState(null);
    const [acctError, setAcctError] = useState('');

    // preload — 대화의 에이전트가 넘긴 첨부(open_case_register). 양식은 비어 있고 첨부 단계에서 시작한다: 공개 범위를 정하고 AI 초안을 누르는 것은 사람이다
    const [sources, setSources] = useState(() => ((preload && preload.sources) || []).filter(s => s && s.id));
    const [urlInput, setUrlInput] = useState('');
    const [fetching, setFetching] = useState(false);
    /** 분류 — **코드 목록은 서버가 준다.** 화면에 또 적으면 서버와 갈라진다. */
    const [topicCatalog, setTopicCatalog] = useState([]);
    const [topicAxes, setTopicAxes] = useState([]);
    const [topics, setTopics] = useState(() => (initial && initial.topics) || []);
    /**
     * 용도·성능 근거·결과 동등성(2026-10-11) — **전부 선택 칸**이다. 사례는 참조·교육·챌린지 예시로 쓰는 느슨한 자료라 다 채우지 않아도 된다.
     * usage 가 null 이면 「정하지 않음」 — 서버가 내용에서 정한다(전/후 SQL·계획이 다 있으면 AI 참조, 아니면 교육). 화면도 같은 규칙으로 미리 보여 준다.
     */
    const [meta, setMeta] = useState(() => ({
        usage: initial && initial.usageDerived === false && Array.isArray(initial.usage) ? initial.usage : null,
        perfBasis: (initial && initial.perfBasis) || '',
        resultSame: (initial && initial.resultSame) || '',
    }));
    const [tab, setTab] = useState(initialTab && TABS.includes(initialTab) ? initialTab : 'basic');
    // 지금 단계 — 새 사례는 첨부부터, 편집·복제·에이전트 초안처럼 내용이 이미 있으면 양식 편집부터
    const [step, setStep] = useState(() => (initialTab === 'redact' ? 'redact' : (initial ? 'edit' : 'attach')));
    const [form, setForm] = useState(() => toForm(initial));
    const [redRows, setRedRows] = useState(() => toRows(initial && initial.redaction));
    /** 「이름 바꾸기 → 양식에 적용」 의 건수 — 저장 때 이력용으로만 보낸다(CaseRedactPanel) */
    const [renameStats, setRenameStats] = useState(null);
    /**
     * 복제본으로 저장할 원본 id — 이름 바꾸기(변조)는 **원본을 두고 복제본에** 한다(사용자 요청 2026-10-11).
     * 상세의 「복제 후 이름 바꾸기」 로 왔으면 처음부터, 원본을 편집하다 이름 바꾸기를 적용하면 그 순간 복제 모드가 된다
     * (서버도 원본에 이름 바꾸기 저장을 409 로 막는다). 이미 복제본인 사례는 그 자리에서 바꿔도 된다.
     */
    const [copyFrom, setCopyFrom] = useState(() => (initial && !initial.id && initial.copyOf ? initial.copyOf : null));
    const [autoCopy, setAutoCopy] = useState(false);
    /** 이름 바꾸기에 쓰는 매핑 규칙 — 원본 폴더 단위로 누적된다(저장 때 적용한 매핑을 더한다) */
    const [renameRuleId, setRenameRuleId] = useState(null);
    const copyTitle = copyFrom && initial ? initial.title : '';
    /** 'map' = 매핑으로 기계 검증 / 'none' = 이미 변조됐거나 바꿀 이름이 없다는 **사람의 선언** */
    const [redactionMode, setRedactionMode] = useState((initial && initial.redactionMode) || 'map');
    const [visibility, setVisibility] = useState((initial && initial.visibility) || 'generic');
    // 범용(남에게 보이는) 사례를 새로 내는 것은 Expert·관리자만(2026-10-29 사용자 유형 — 서버 저장 경로가 같은 판정으로 막는다).
    // 이미 범용인 내 사례를 고치는 것은 그대로 둔다. 답을 받기 전에는 바꾸지 않는다(ready)
    const access = useMonitoringAccess(true, IS_STANDALONE);
    const genericLocked = access.ready && !access.canPublish && !(initial && initial.id && initial.visibility === 'generic');
    useEffect(() => { if (genericLocked && visibility === 'generic') setVisibility('internal'); }, [genericLocked, visibility]);
    const [status, setStatus] = useState((initial && initial.status) || 'final');
    // 폴더 — 편집이면 그 사례의 것, 새 사례면 목록에서 고른 폴더. 여러 사례를 이어 등록해도 그대로 남는다(같은 폴더에 넣는 일이 많다)
    const [folder, setFolder] = useState(() => (initial && initial.folder) || defaultFolder || '');
    const [folderList, setFolderList] = useState([]);
    /** 매핑 규칙의 원본 폴더 — 복제·편집이면 원본 사례의 폴더, 새 사례면 지금 고른 폴더 */
    const ruleFolder = (initial && initial.folder) || folder || '';
    useEffect(() => {
        // 폴더 후보 — 못 읽어도 직접 적을 수 있다
        Promise.resolve().then(() => getCaseFolders())
            .then(r => setFolderList(((r && r.folders) || []).map(f => f.path)))
            .catch(() => {});
    }, []);
    const [instruction, setInstruction] = useState(() => (preload && preload.instruction) || '');

    const [uploading, setUploading] = useState(false);
    const [drafting, setDrafting] = useState(false);
    const [saving, setSaving] = useState(false);
    /**
     * 초안·저장 실패는 **지워지지 않는 자리**에 남긴다.
     *
     * 토스트로만 띄웠더니 몇 초 뒤 사라져서, 사용자에게는 "아무 일도 안 일어났다" 로 보였다
     * (실제 신고 2026-09-10). 게다가 실패의 근거 — 모델이 실제로 뭐라고 했는지, 어느 첨부를
     * 못 읽었는지 — 는 문구 한 줄에 담기지 않는다. 서버가 준 것을 그대로 펼쳐 둔다.
     */
    const [draftError, setDraftError] = useState(null);
    // 자료 하나에 사례가 여럿일 수 있다(블로그 목록 페이지, 발표자료 …). 한 양식에 뭉치면
    // 전/후가 짝이 맞지 않아 사례 전체가 쓸모없어지므로, **목록을 보여 주고 한 건씩** 등록한다.
    const [caseList, setCaseList] = useState([]);
    const [caseIndex, setCaseIndex] = useState(0);
    // 이미 저장한 사례. 남은 것을 이어서 등록하려면 어디까지 했는지 보여야 한다.
    const [caseDone, setCaseDone] = useState([]);
    /**
     * **사례별 작업본.** 초안을 한 번 받은 사례는 고친 내용·매핑·분류·대조 결과까지 여기 둔다.
     * 다른 사례로 갔다가 돌아오면 AI 를 다시 부르지 않고 이것을 되돌린다 — 다시 부르면 호출이
     * 낭비되고 사람이 고친 것이 사라진다(실제 신고 2026-09-13: 목록을 누를 때마다 다시 쓰기가 돌았다).
     * 제목을 함께 담아 두고 **제목이 같을 때만** 되돌린다 — 재작성으로 목록이 바뀌면 번호가 다른 사례를 가리킨다.
     */
    const [stash, setStash] = useState({});
    // 목록에서 고른 사례의 초안을 받는 중이면 그 번호. 버튼의 「작성 중」만으로는 무엇을 쓰는지 안 보인다.
    const [pendingIndex, setPendingIndex] = useState(null);
    // **대조.** 답을 가린 채 다시 뽑은 결과와 지금 사례의 차이. 판정이 아니라 차이다 —
    // 점수도 매기지 않고 저장을 막지도 않는다.
    const [checking, setChecking] = useState(false);
    const [checkError, setCheckError] = useState(null);
    const [check, setCheck] = useState(null);
    const [saveError, setSaveError] = useState(null);

    /**
     * 그림 — 원문의 설명용 이미지(2026-10-12). 올리면 서버에 대기 상태로 들어가고, 저장할 때 이 목록(순서·설명·공개)대로 사례에 붙는다.
     * 그림은 사람이 읽는 자료다: AI 로 나가지 않고, 범용 사례에서도 「공개」 를 켠 것만 남에게 보인다(그림 속 이름은 기계가 검증할 수 없다).
     */
    const [figures, setFigures] = useState(() => ((initial && initial.figures) || []).map(f => ({ id: f.id, caption: f.caption || '', shared: !!f.shared, filename: f.filename || '', width: f.width || 100, bg: f.bg || 'light' })));
    const [figBusy, setFigBusy] = useState(false);
    const figInputRef = useRef(null);
    const addFigures = useCallback(async (files) => {
        const imgs = [...(files || [])].filter(f => String(f.type || '').startsWith('image/'));
        if (!imgs.length) return;
        setFigBusy(true);
        try {
            const r = await uploadCaseFigures(imgs);
            const ok = (r.figures || []).filter(x => x.id);
            setFigures(prev => [...prev, ...ok.map(x => ({ id: x.id, caption: '', shared: false, filename: x.filename || '', width: 100, bg: 'light' }))].slice(0, MAX_FIGURES));
            for (const b of (r.figures || [])) if (!b.id) toast.error(`${b.filename || ''}: ${b.error}`);
        } catch (err) { toast.error(err.message); } finally { setFigBusy(false); }
    }, []);
    /**
     * 원본 PPTX 의 **도식을 그림으로**(2026-10-13) — 슬라이드의 도식은 도형으로 그린 것이라 그림 파일로 꺼낼 수 없다. 서버가 슬라이드를 SVG 로
     * 그려 주고(tuningCase/slideRender), 여기서 PNG 로 바꿔 사례의 그림으로 올린다. AI 와 무관한 길이다(그림은 모델로 가지 않는다).
     * 초안이 「이 사례는 슬라이드 12~14」 라고 알려 주면 그중 도식인 것을 자동으로 넣고, 그림 탭에서 직접 고를 수도 있다.
     * 같은 슬라이드는 한 번만 넣는다(`slideKey`). 넣은 것은 여느 그림처럼 설명·순서를 고치거나 지울 수 있다.
     */
    const deck = useMemo(() => { const d = sources.filter(x => x.kind === 'pptx' && !x.parentId); return d.length === 1 ? d[0] : null; }, [sources]);
    const [slideList, setSlideList] = useState(null);          // { id, slides } — 그 원본의 슬라이드 목록
    const [slidePick, setSlidePick] = useState(false);         // 그림 탭의 「슬라이드에서 고르기」 펼침
    const [slideAll, setSlideAll] = useState(false);           // 도식만 / 전부
    const [slideBusy, setSlideBusy] = useState(null);          // 그리는 중인 슬라이드 번호
    const figuresRef = useRef(figures);
    figuresRef.current = figures;
    const loadSlides = useCallback(async (src) => {
        if (!src) return [];
        if (slideList && slideList.id === src.id) return slideList.slides;
        const r = await getSourceSlides(src.id);
        setSlideList({ id: src.id, slides: r.slides || [] });
        return r.slides || [];
    }, [slideList]);
    /** 슬라이드들을 그림으로 올린다. 넣은 수를 돌려준다 */
    const addSlideFigures = useCallback(async (src, metas) => {
        let added = 0;
        for (const m of metas) {
            const key = `${src.id}:${m.no}`;
            if (figuresRef.current.length + added >= MAX_FIGURES) break;
            if (figuresRef.current.some(f => f.slideKey === key)) continue;
            setSlideBusy(m.no);
            try {
                const one = await getSourceSlide(src.id, m.no);
                const file = await svgToPngFile(one.svg, `slide-${m.fileNo || m.no}.png`);
                const r = await uploadCaseFigures([file]);
                const ok = (r.figures || []).find(x => x.id);
                if (!ok) { const bad = (r.figures || [])[0]; throw new Error((bad && bad.error) || 'FIGURE_FAILED'); }
                const caption = [t('assistant:caseFig.slideNo', { n: m.fileNo || m.no }), one.title || m.title].filter(Boolean).join(' — ').slice(0, 300);
                setFigures(prev => (prev.some(f => f.slideKey === key) ? prev : [...prev, { id: ok.id, caption, shared: false, filename: ok.filename || file.name, width: 100, bg: 'light', slideKey: key }].slice(0, MAX_FIGURES)));
                added += 1;
            } catch (err) {
                toast.error(t('assistant:caseFig.slideFailed', { n: m.fileNo || m.no, message: err.message }));
            }
        }
        setSlideBusy(null);
        return added;
    }, [t]);
    /** 초안이 알려 준 슬라이드 가운데 도식인 것을 넣는다 — 못 넣어도 초안은 그대로다(그림은 덤이다) */
    const autoSlideFigures = useCallback(async (slideNos) => {
        if (!deck || !Array.isArray(slideNos) || !slideNos.length) return;
        try {
            const list = await loadSlides(deck);
            const picked = pickDiagramSlides(list, slideNos, AUTO_SLIDE_FIGURES);
            if (!picked.length) return;
            const n = await addSlideFigures(deck, picked);
            if (n) toast.info(t('assistant:caseFig.slideAuto', { count: n }), { autoClose: 9000 });
        } catch (_e) { /* 그림을 못 넣었다고 초안을 막지 않는다 — 그림 탭에서 직접 고를 수 있다 */ }
    }, [deck, loadSlides, addSlideFigures, t]);
    const openSlidePick = useCallback(async () => {
        setSlidePick(v => !v);
        if (deck) { try { await loadSlides(deck); } catch (err) { toast.error(err.message); } }
    }, [deck, loadSlides]);

    const setFig = (i, patch) => setFigures(prev => prev.map((f, j) => (j === i ? { ...f, ...patch } : f)));
    const moveFig = (i, d) => setFigures((prev) => {
        const j = i + d;
        if (j < 0 || j >= prev.length) return prev;
        const next = [...prev]; [next[i], next[j]] = [next[j], next[i]]; return next;
    });

    const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));
    // 서식 단추가 고른 범위를 읽는 글 칸 — 칸마다 고정된 ref(렌더마다 새로 만들면 단추가 비어 있는 ref 를 본다)
    const fieldRefs = useRef({});
    const fieldRef = (k) => { if (!fieldRefs.current[k]) fieldRefs.current[k] = { current: null }; return fieldRefs.current[k]; };
    const hasDraft = useMemo(() => FIELDS.some(f => String(form[f.key] || '').trim()), [form]);
    /** 단계에 결과가 있는지(리본의 ✓) — 첨부·초안·필수 칸·적용한 이름 바꾸기·대조 결과 */
    const stepDone = {
        attach: sources.length > 0,
        draft: caseList.length > 0,
        edit: !!(String(form.title || '').trim() && String(form.body || '').trim()),
        redact: !!renameStats,
        check: !!check,
    };
    /** 탭에 내용이 있는지 — 접힌 탭의 내용이 보이지 않으니 점으로라도 알려 준다. */
    const filled = useMemo(() => {
        const o = {};
        for (const f of FIELDS) if (String(form[f.key] || '').trim()) o[f.tab] = true;
        return o;
    }, [form]);

    /**
     * 실행계획 표시 점검 — 본문·유의사항의 「Id n」 이 계획에 없어 표시가 빠지거나 다른 계획에 붙는 경우(2026-10-11).
     * 상세 화면 문서와 **같은 함수**(caseRefs)로 판정한다. 저장을 막지 않는다 — 알리기만.
     */
    const refIssues = useMemo(() => {
        const { refs } = caseRefs(form);
        return refs ? refs.issues : [];
    }, [form]);

    /** 고른 AI 의 계정 상태. **판정은 패널과 같은 함수**가 한다(accountBadge.js). */
    const [acctSeq, setAcctSeq] = useState(0);
    useEffect(() => {
        let alive = true;
        setAccount(null);
        setAcctError('');
        getAccount(ai)
            .then(a => { if (alive) setAccount(a); })
            .catch(err => { if (alive) setAcctError(err.message); });
        return () => { alive = false; };
    }, [ai, acctSeq]);
    const acct = useMemo(() => describeAccount({ account, error: acctError }, t), [account, acctError, t]);
    /** 고를 수 있는 AI 는 **서버가 말해 준다** — 화면에 목록을 또 적으면 서버와 갈라진다. */
    const aiChoices = (account && account.providers) || [ai];

    const addFiles = useCallback(async (files) => {
        if (!files || !files.length) return;
        setUploading(true);
        try {
            const r = await uploadTuningSources(files);
            const ok = (r.sources || []).filter(s => s.id);
            setSources(prev => [...prev, ...ok]);
            for (const b of (r.sources || [])) {
                if (!b.id) toast.error(`${b.filename}: ${b.error}`);
            }
        } catch (err) {
            toast.error(err.message);
        } finally { setUploading(false); }
    }, []);

    useEffect(() => {
        let alive = true;
        getCaseTopics()
            .then(r => { if (alive) { setTopicCatalog(r.topics || []); setTopicAxes(r.axes || []); } })
            .catch(() => { /* 분류를 못 읽어도 등록은 되어야 한다 */ });
        return () => { alive = false; };
    }, []);

    /**
     * URL 을 원본으로 등록한다. **본문 이미지까지 가져온다** — 튜닝 블로그는 SQL 과 실행계획을
     * 이미지로 올리는 경우가 많아, 글만 가져오면 "무엇을 고쳤다" 는 서술만 남는다.
     */
    const addUrl = useCallback(async () => {
        const url = urlInput.trim();
        if (!url) return;
        setFetching(true);
        setDraftError(null);
        try {
            const r = await addTuningSourceUrl({ url });
            setSources(prev => [...prev, ...(r.sources || [])]);
            setUrlInput('');
            toast.success(t('assistant:caseForm.urlAdded', { count: (r.sources || []).length }));
            if (r.note) toast.warning(r.note);
        } catch (err) {
            const b = err.body || {};
            setDraftError({ message: err.message, code: b.errorType });
        } finally { setFetching(false); }
    }, [urlInput, t]);

    // 에이전트가 보낸 양식 — 바로 덮지 않고 들고 있다가 사용자가 「반영」 을 누르면 채운다(빈 칸은 지금 것을 지우지 않는다)
    const [incoming, setIncoming] = useState(null);
    useEffect(() => {
        if (!agentDraft) return;
        const fields = {};
        for (const f of FIELDS) if (typeof agentDraft[f.key] === 'string' && agentDraft[f.key].trim()) fields[f.key] = agentDraft[f.key];
        setIncoming({ fields, topics: agentDraft.topics || [], redaction: agentDraft.redaction || null, nonce: agentDraft.nonce });
    }, [agentDraft]);
    const applyIncoming = useCallback(() => {
        if (!incoming) return;
        setForm(prev => alignPlans({ ...prev, ...incoming.fields }));
        if (incoming.topics.length) setTopics(incoming.topics);
        if (incoming.redaction) setRedRows(toRows(incoming.redaction));
        setIncoming(null);
        setStep('edit');
    }, [incoming]);
    /** 요청과 함께 그 턴에만 모델에게 갈 이 화면의 내용 — 지금 양식과 분류·공개 범위(매핑·폴더·원본 이름은 싣지 않는다) */
    const screenForAgent = useCallback(() => ({
        kind: 'caseRegister',
        text: JSON.stringify({ ...form, topics, visibility, redactionMode }, null, 1),
    }), [form, topics, visibility, redactionMode]);

    // 새 사례의 기본값 — 지난 등록들에서 뚜렷이 쏠린 선택(진화 기록의 작업 절차 경험). 편집·복제·에이전트가 채운 양식에는 걸지 않는다.
    // 되돌리기 쉬운 기본값이라 자동으로 채우되 그 사실을 말한다 — 「공개 범위」 단계에서 그대로 바꿀 수 있다
    const [learnedDefaults, setLearnedDefaults] = useState(null);
    useEffect(() => {
        if (initial) return undefined;
        let alive = true;
        getExperienceDefaults('caseRegister').then((r) => {
            const d = (r && r.defaults) || {};
            if (!alive || !Object.keys(d).length) return;
            if (d.visibility) setVisibility(d.visibility);
            if (d.redactionMode) setRedactionMode(d.redactionMode);
            setLearnedDefaults(d);
        }).catch(() => { /* 기본값을 못 받아도 등록은 원래 기본값으로 된다 */ });
        return () => { alive = false; };
    }, [initial]);

    const removeSource = useCallback(async (id) => {
        setSources(prev => prev.filter(s => s.id !== id && s.parentId !== id));   // 문서에서 꺼낸 그림도 함께
        try { await deleteTuningSource(id); } catch (_e) { /* 목록에서 뺐으면 충분하다 */ }
    }, []);

    /** 초안 생성과 재작성은 **같은 호출**이다 — 지금 양식이 있으면 그것을 고쳐 준다. */
    const runDraft = useCallback(async ({ index = null } = {}) => {
        // index 가 오면 **다른 사례를 새로 뽑는 것**이다 — 지금 양식을 고쳐 달라는 재작성이 아니다.
        // 그때 current 를 함께 보내면 앞 사례의 쿼리·계획이 새 사례에 섞인다.
        const picking = index !== null && index !== caseIndex;
        setDrafting(true);
        setDraftError(null);
        setPendingIndex(picking ? index : null);
        try {
            const r = await buildCaseDraft({
                sourceIds: sources.map(s => s.id),
                visibility,
                // 「변조하지 않음」이면 초안도 이름을 그대로 둔다 — 이미 변조된 자료를 한 번 더
                // 바꾸면 원본과의 대응이 끊긴다
                redact: redactionMode === 'map',
                current: hasDraft && !picking ? form : null,
                instruction: picking ? '' : instruction.trim(),
                provider: ai,
                caseIndex: index === null ? caseIndex : index,
            });
            // 매핑은 양식 칸이 아니다 — 섞어 두면 저장할 때 어디로 갈지가 두 갈래가 된다
            const { redaction, topics: drafted, ...fields } = r.fields || {};
            // 사례를 갈아탈 때는 **덮어쓰기가 아니라 새로 채운다** — 앞 사례의 남은 칸이
            // 새 사례에 붙어 있으면 전/후가 섞인 가짜 사례가 된다
            setForm(prev => alignPlans(picking ? { ...toForm(null), ...fields } : { ...prev, ...fields }));
            // 그림은 사례의 것이다 — 다른 사례를 새로 뽑으면 앞 사례의 그림을 들고 가지 않는다(앞 사례 것은 작업본에 남아 있다)
            if (picking) { setRedRows([]); setTopics([]); setCheck(null); setCheckError(null); setFigures([]); figuresRef.current = []; }
            // 다른 사례를 고를 때는 **목록을 갈아 끼우지 않는다.** 호출마다 모델이 제목을 조금씩 달리 적으면
            // 번호가 흔들려 받아 둔 초안이 엉뚱한 사례에 붙는다. 목록은 처음 초안(또는 재작성)이 정한다.
            if (Array.isArray(r.caseList) && (!picking || !caseList.length)) setCaseList(r.caseList);
            if (Number.isInteger(r.caseIndex)) setCaseIndex(r.caseIndex);
            if (redaction) setRedRows(toRows(redaction));
            // 모델이 고른 분류는 **제안**이다 — 아래 칩에서 사람이 고치게 둔다
            if (drafted && drafted.length) setTopics(drafted);
            setInstruction('');
            toast.success(t('assistant:caseForm.drafted'));
            // 이 사례가 실린 슬라이드 가운데 **도식**을 그림으로 넣는다(기다리지 않는다 — 양식 편집은 바로 시작한다)
            if (Array.isArray(r.slides) && r.slides.length) autoSlideFigures(r.slides);
            // 양식 편집으로 넘어간다(사례가 여럿이면 그 목록도 편집 단계 머리에 있다)
            setStep('edit');
            // 이미지는 가드레일이 검사하지 못한다 — 글자만 검사한다(21-ai-guardrail §8)
            if (r.imageUnscanned) toast.info(t('common:grd.imageUnscanned', { count: r.imageUnscanned }));
            // 한 번에 읽힐 그림 수에는 상한이 있다 — 문서에서 꺼낸 그림이 많으면 뒤의 것은 읽지 않았다고 말한다(조용히 빠지면 계획이 왜 비었는지 모른다)
            if ((r.imagesSkipped || []).length) toast.warning(t('assistant:caseForm.imagesSkipped', { count: r.imagesSkipped.length }), { autoClose: 8000 });
        } catch (err) {
            // 서버가 근거를 함께 준다: 모델의 실제 응답(raw), 못 읽은 첨부(unusable)
            const b = err.body || {};
            setDraftError({ message: err.message, code: b.errorType, raw: b.raw, unusable: b.unusable });
        } finally { setDrafting(false); setPendingIndex(null); }
    }, [sources, visibility, redactionMode, form, hasDraft, instruction, ai, caseIndex, caseList.length, t, autoSlideFigures]);

    /**
     * 목록에서 다른 사례로 옮긴다. 이미 초안을 받은 사례면 **작업본을 되돌리고 AI 를 부르지 않는다.**
     * 떠나는 사례도 작업본으로 남긴다 — 옮기는 것만으로 고친 내용이 사라지면 안 된다(그래서 묻지도 않는다).
     */
    const switchCase = useCallback((i) => {
        if (i === caseIndex || drafting) return;
        const here = caseList[caseIndex];
        const there = caseList[i];
        const next = { ...stash };
        if (hasDraft && here) next[caseIndex] = { title: here.title, form, redRows, topics, check, figures };
        const kept = next[i];
        if (kept && there && kept.title === there.title) {
            delete next[i];
            setStash(next);
            setForm(kept.form);
            setRedRows(kept.redRows);
            setTopics(kept.topics);
            setCheck(kept.check);
            setFigures(kept.figures || []);
            setCheckError(null);
            setDraftError(null);
            setCaseIndex(i);
            setStep('edit');
            return;
        }
        setStash(next);
        runDraft({ index: i });
    }, [caseIndex, drafting, caseList, stash, hasDraft, form, redRows, topics, check, figures, runDraft]);

    /**
     * **답을 가린 채 다시 풀어 본다.** 개선 전 쿼리·실행계획만 새 호출에 주고 증상·원인·처방을
     * 다시 뽑게 한 뒤 지금 분류와 대조한다. 서버의 `holdout` 이 나가는 칸을 정하므로 여기서
     * 양식을 통째로 보내도 개선 후 내용은 실리지 않는다.
     */
    const runCheck = useCallback(async () => {
        setChecking(true);
        setCheckError(null);
        try {
            const r = await validateCaseDraft({ fields: form, topics, provider: ai });
            setCheck(r);
        } catch (err) {
            const b = err.body || {};
            setCheckError({ message: err.message, code: b.errorType, raw: b.raw });
        } finally { setChecking(false); }
    }, [form, topics, ai]);

    /** 축별로 고른 것. 증상·원인·처방은 **권장**이다 — 비어도 저장되고 「분류 미완」 으로 보인다(2026-10-11 필수 → 권장). 영역은 선택 축. */
    const axisOf = useMemo(() => {
        const m = {};
        for (const t of topicCatalog) m[t.code] = t.axis;
        return m;
    }, [topicCatalog]);
    // 대조 결과는 코드로 온다 — 화면에는 유형 이름으로 보여야 한다(코드는 사람이 못 읽는다)
    const labelOf = useCallback(
        (code) => { const tp = topicCatalog.find(x => x.code === code); return (tp && tp.label) || code; },
        [topicCatalog],
    );
    const missingAxes = useMemo(
        () => topicAxes.filter(a => !a.optional).map(a => a.id).filter(id => !topics.some(c => axisOf[c] === id)),
        [topicAxes, topics, axisOf],
    );

    const save = useCallback(async () => {
        if (!form.title.trim() || !form.body.trim()) {
            // 필수 칸은 기본 탭에 있다. 다른 탭을 보고 있으면 무엇이 비었는지 보이지 않는다.
            setTab('basic');
            toast.warning(t('assistant:caseForm.needTitleBody'));
            return;
        }
        setSaving(true);
        setSaveError(null);
        try {
            const r = await saveTuningCase({
                ...form,
                // 복제 모드면 새 사례로(원본 id 는 copyOf 로만) — 원본은 그대로 둔다
                id: copyFrom ? undefined : (initial ? initial.id : undefined),
                copyOf: copyFrom || undefined,
                visibility,
                status,
                // 원본을 함께 주면 서버가 **매핑에 없는 식별자**까지 경고해 준다
                sourceId: sources[0] ? sources[0].id : (initial ? initial.sourceId : undefined),
                // 원본 파일명은 서버가 이 id 들로 찾아 사례에 남긴다(첨부가 없으면 보내지 않는다 — 편집 때 지우지 않게)
                // 원본 파일명으로 남길 것 — 문서에서 꺼낸 그림(딸린 원본)은 뺀다(문서 이름이 이미 있다)
                sourceIds: sources.length ? sources.filter(s => !s.parentId).map(s => s.id) : undefined,
                topics,
                // 용도는 정했을 때만 보낸다(안 보내면 서버가 그대로 두거나 내용에서 정한다). 근거 칸은 이 화면이 값을 쥔다
                usage: meta.usage === null ? undefined : meta.usage,
                perfBasis: meta.perfBasis || null,
                resultSame: meta.resultSame || null,
                // 그림 — 이 화면이 목록을 쥔다(순서·설명·공개). 뺀 그림은 서버가 지운다
                figures: figures.map(f => ({ id: f.id, caption: f.caption, shared: visibility === 'generic' && f.shared, width: f.width, bg: f.bg })),
                redactionMode: visibility === 'generic' ? redactionMode : undefined,
                redaction: visibility === 'generic' && redactionMode === 'map' ? toMap(redRows) : undefined,
                // 「이름 바꾸기」 를 적용했으면 건수만(이력용). 내부 사례에는 매핑(원래 이름)을 싣지 않는다
                renameStats: renameStats || undefined,
                // 적용한 매핑을 고른 규칙(원본 폴더 단위)에 누적 — 이름 바꾸기를 적용했을 때만 보낸다
                renameRuleId: renameStats && renameRuleId ? renameRuleId : undefined,
                renameMapping: renameStats && renameRuleId ? toRows(toMap(redRows)) : undefined,
                folder,
            });
            if (folder && onFoldersChanged) onFoldersChanged();
            toast.success(t('assistant:caseForm.saved'));
            if (r.warning) toast.warning(r.warning);
            if (r.rule && r.rule.error) toast.warning(t('assistant:caseRule.mergeFailed', { message: r.rule.error }));
            else if (r.rule) toast.info(t('assistant:caseRule.merged', { name: r.rule.name, added: r.rule.added, changed: r.rule.changed, count: r.rule.count }));
            // 자료에 사례가 더 있으면 **화면을 닫지 않는다.** 닫으면 첨부와 찾은 목록이 사라져
            // 나머지를 등록하려고 같은 자료를 처음부터 다시 올려야 한다.
            const more = caseList.length > 1;
            if (more) {
                setCaseDone(prev => (prev.includes(caseIndex) ? prev : [...prev, caseIndex]));
                setStep('draft');   // 남은 사례를 고르러 — 목록은 초안 단계에 있다
                setForm(toForm(null));
                setRedRows([]);
                setTopics([]);
                setMeta({ usage: null, perfBasis: '', resultSame: '' });
                setFigures([]);
                setCheck(null);
                setSaveError(null);
            }
            onSaved(r.case, { keepOpen: more });
        } catch (err) {
            // 서버가 남은 식별자를 알려 준다 — 무엇이 남았는지 알아야 고칠 수 있다
            const b = err.body || {};
            setSaveError({ message: err.message, code: b.errorType, leaked: b.leaked });
        } finally { setSaving(false); }
    }, [form, initial, visibility, status, sources, topics, meta, figures,
        redactionMode, redRows, caseList.length, caseIndex, onSaved, t, folder, onFoldersChanged, renameStats, copyFrom, renameRuleId]);

    /** 이름 바꾸기 적용 — 원본(복제본이 아닌 저장된 사례)이면 그 순간 복제 모드로. 되돌리면 자동으로 들어온 복제 모드도 푼다 */
    const applyRename = useCallback((next, stats) => {
        setForm(prev => ({ ...prev, ...next }));
        setRenameStats(stats);
        if (stats && initial && initial.id && !initial.copyOf && !copyFrom) {
            setCopyFrom(initial.id); setAutoCopy(true);
            toast.info(t('assistant:caseCopy.switched'));
        } else if (!stats && autoCopy) { setCopyFrom(null); setAutoCopy(false); }
    }, [initial, copyFrom, autoCopy, t]);

    /** 자료에 사례가 여럿일 때의 목록 — 초안 단계와 양식 편집 단계 둘 다에 보인다(편집하면서 어느 사례인지 보고 갈아탄다) */
    const caseListView = caseList.length > 1 ? (
        <div className="tcf-caselist">
            <div className="tcf-caselist-head">
                {pendingIndex !== null
                    ? t('assistant:caseForm.caseDrafting', { index: pendingIndex + 1 })
                    : t('assistant:caseForm.multiFound', { count: caseList.length, index: caseIndex + 1 })}
            </div>
            <div className="tcf-caselist-items">
                {caseList.map((c, i) => {
                    const drafted = i !== caseIndex && !!stash[i] && stash[i].title === c.title;
                    const needsAi = i !== caseIndex && !drafted;
                    return (
                        <button
                            key={`${i}-${c.title}`}
                            type="button"
                            className={`tcf-case-item${i === caseIndex ? ' tcf-case-current' : ''}${caseDone.includes(i) ? ' tcf-case-done' : ''}${pendingIndex === i ? ' tcf-case-pending' : ''}`}
                            disabled={drafting}
                            title={needsAi ? `${c.title} — ${t('assistant:caseForm.casePickHint')}` : c.title}
                            onClick={() => switchCase(i)}
                        >
                            <span className="tcf-case-no">
                                {pendingIndex === i ? <FiLoader size={10} className="tcf-spin" /> : (caseDone.includes(i) ? '✓' : i + 1)}
                            </span>
                            <span className="tcf-case-title">{c.title}</span>
                            {drafted ? <span className="tcf-case-badge">{t('assistant:caseForm.caseDrafted')}</span> : null}
                        </button>
                    );
                })}
            </div>
            <div className="tcf-hint">{t('assistant:caseForm.multiHint')}</div>
        </div>
    ) : null;

    return (
        <div className="tcf">
            {/* 복제 모드 — 원본은 그대로 두고 복제본으로 저장한다. 복제본의 폴더를 여기서 고른다(2단계의 폴더 칸과 같은 값) */}
            {copyFrom && (
                <div className="tcf-copyBanner tcf-fade" role="status">
                    <div><FiCopy size={12} /> <b>{t('assistant:caseCopy.banner')}</b>{copyTitle ? <span className="tcf-hint"> — {copyTitle}</span> : null}</div>
                    <label className="tcf-copyFolder">
                        {t('assistant:caseCopy.folder')}
                        <FolderPicker value={folder} options={folderList} onChange={setFolder} t={t} />
                    </label>
                </div>
            )}
            {/* 단계 리본 — SQL 튜닝 화면과 같은 모양. ✓ = 그 단계에 결과가 있다, 「선택」 = 건너뛰어도 저장된다 */}
            <nav className="stn-ribbon tcf-ribbon" role="tablist" aria-label={t('assistant:caseForm.stepsLabel')}>
                {STEPS.map((id, i) => (
                    <button key={id} type="button" role="tab" aria-selected={step === id}
                        className={`stn-tab${step === id ? ' on' : ''}`} onClick={() => setStep(id)}>
                        {`${i + 1}. `}{t(`assistant:caseForm.steps.${id}`)}
                        {stepDone[id] ? <span className="tcf-stepDone"> ✓</span> : null}
                        {OPTIONAL_STEPS.includes(id) && !stepDone[id] ? <span className="tcf-stepOpt"> · {t('assistant:caseForm.optional')}</span> : null}
                    </button>
                ))}
            </nav>
            {/* 지금 단계에서 할 일 — 리본 바로 아래 한 줄(사용자 요청 2026-10-11). 단계가 바뀌면 부드럽게 갈아 끼운다 */}
            <div className="tcf-stepHelp" key={`help-${step}`} role="note">{t(`assistant:caseForm.stepHelp.${step}`)}</div>
            {/* 1 첨부 — 편집 중에도 자료를 더 붙일 수 있다 */}
            {step === 'attach' && (
            <section className="tcf-step">
                <div className="tcf-head">
                    <span className="tcf-no">1</span>{t('assistant:caseForm.stepAttach')}
                </div>
                {/* 대화의 에이전트가 첨부를 넘겼다 — 여기서부터는 화면의 절차다(공개 범위를 정하고 AI 초안을 누른다) */}
                {preload && sources.length > 0 ? <div className="tcf-stepHelp" role="note">{t('assistant:caseForm.fromAgent', { count: sources.length, images: sources.filter(s => s.kind === 'image').length })}</div> : null}
                <div className="tcf-sources">
                    <button
                        type="button" className="tcf-btn" disabled={uploading}
                        onClick={() => inputRef.current && inputRef.current.click()}
                    >
                        <FiPaperclip size={11} /> {uploading ? t('assistant:caseForm.uploading') : t('assistant:caseForm.pickFiles')}
                    </button>
                    <input
                        ref={inputRef} type="file" multiple accept={ACCEPT} style={{ display: 'none' }}
                        onChange={(e) => { addFiles([...e.target.files]); e.target.value = ''; }}
                    />
                    {/* 문서에서 꺼낸 그림은 칩으로 따로 세우지 않는다 — 문서 칩에 장 수만(패널의 첨부 줄과 같은 규칙) */}
                    {topSources(sources).map(s => (
                        <span key={s.id} className="tcf-chip" title={s.note || ''}>
                            {s.filename}
                            {childCount(sources, s.id) ? <span className="tcf-chipInner" title={t('assistant:case.innerImagesHint', { count: childCount(sources, s.id) })}>{t('assistant:case.innerImages', { count: childCount(sources, s.id) })}</span> : null}
                            <button type="button" onClick={() => removeSource(s.id)} title={t('common:ui.delete')}>
                                <FiX size={10} />
                            </button>
                        </span>
                    ))}
                    {!sources.length ? <span className="tcf-hint">{t('assistant:caseForm.attachHint')}</span> : null}
                </div>
                {/* **URL 도 원본이다.** 사례는 블로그·사내 위키에 글로 있는 경우가 많다.
                    본문 이미지까지 함께 가져온다 — 거기 실행계획이 들어 있다. */}
                <div className="tcf-row tcf-urlRow">
                    <input
                        className="tcf-input" type="url" value={urlInput}
                        placeholder={t('assistant:caseForm.urlPlaceholder')}
                        onChange={(e) => setUrlInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addUrl(); } }}
                    />
                    <button type="button" className="tcf-btn" disabled={fetching || !urlInput.trim()} onClick={addUrl}>
                        {fetching ? <FiLoader size={11} className="tcf-spin" /> : <FiLink size={11} />}
                        {fetching ? t('assistant:caseForm.urlFetching') : t('assistant:caseForm.urlAdd')}
                    </button>
                </div>
                <div className="tcf-hint">{t('assistant:caseForm.urlHint')}</div>
            </section>
            )}

            {/* 2 공개 범위 — **초안보다 먼저 정한다.**
                범용이면 모델이 시스템 고유 이름을 바꿔서 쓰고, 내부면 그대로 쓴다. 뒤에 두면
                초안을 다 만들어 놓고 "사실 공개용이었다" 며 처음부터 다시 하게 된다. */}
            {step === 'visibility' && (
            <section className="tcf-step">
                <div className="tcf-head">
                    <span className="tcf-no">2</span>{t('assistant:caseForm.stepVisibility')}
                </div>
                <div className="tcf-row">
                    <label className="tcf-choice" title={genericLocked ? t('assistant:caseForm.genericExpertOnly') : undefined}>
                        <input type="radio" checked={visibility === 'generic'} disabled={genericLocked} onChange={() => setVisibility('generic')} />
                        {t('assistant:caseForm.visGeneric')}
                    </label>
                    <label className="tcf-choice">
                        <input type="radio" checked={visibility === 'internal'} onChange={() => setVisibility('internal')} />
                        {t('assistant:caseForm.visInternal')}
                    </label>
                </div>
                <div className="tcf-hint">
                    {visibility === 'generic' ? t('assistant:caseForm.genericNote') : t('assistant:caseForm.internalNote')}
                </div>
                {genericLocked && <div className="tcf-hint">{t('assistant:caseForm.genericExpertOnly')}</div>}
                {/* 지난 등록들에서 주로 고른 값으로 채웠다 — 조용히 바꾸지 않는다(도움말 › 진화 기록에서 끌 수 있다) */}
                {learnedDefaults ? <div className="tcf-hint" role="note">{t('assistant:caseForm.learnedDefaults')}</div> : null}
                {/* 공개한다고 늘 변조해야 하는 것은 아니다 — 이미 변조된 자료일 수 있다 */}
                {visibility === 'generic' ? (
                    <>
                        <div className="tcf-row tcf-redModes">
                            <label className="tcf-choice">
                                <input type="radio" checked={redactionMode === 'map'} onChange={() => setRedactionMode('map')} />
                                {t('assistant:caseForm.redModeMap')}
                            </label>
                            <label className="tcf-choice">
                                <input type="radio" checked={redactionMode === 'none'} onChange={() => setRedactionMode('none')} />
                                {t('assistant:caseForm.redModeNone')}
                            </label>
                        </div>
                        <div className="tcf-hint">
                            {redactionMode === 'none'
                                ? t('assistant:caseForm.redNoneHint')
                                : t('assistant:caseForm.redMapHint')}
                        </div>
                    </>
                ) : null}
                {/* 폴더 — 주인만 보는 정리 경로(남·AI 에게는 나가지 않는다). 없는 이름을 적으면 저장할 때 만들어진다 */}
                <div className="tcf-row tcf-folderRow">
                    <label className="tcf-label" htmlFor="tcf-folder">{t('assistant:caseFolder.field')}</label>
                    <FolderPicker id="tcf-folder" value={folder} options={folderList} onChange={setFolder} t={t} />
                </div>
                <div className="tcf-hint">{t('assistant:caseFolder.fieldHint')}</div>
            </section>
            )}

            {/* 3 분석 — **누를 때만** LLM 을 부른다 */}
            {step === 'draft' && (
            <section className="tcf-step">
                <div className="tcf-head">
                    <span className="tcf-no">3</span>
                    {hasDraft ? t('assistant:caseForm.stepRewrite') : t('assistant:caseForm.stepAnalyze')}
                </div>
                {/* **누구 계정으로 도는가.** 누르기 전에 보여야 한다 — 로그인이 없으면 412 로
                    죽고, 구독 대신 API 키로 돌면 종량으로 청구된다. 바꿀 수도 있어야 한다. */}
                <div className="tcf-row tcf-aiRow">
                    <select
                        className="tcf-select" value={ai} onChange={(e) => setAi(e.target.value)}
                        title={t('assistant:caseForm.aiPick')}
                    >
                        {aiChoices.map(p => (
                            <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                        ))}
                    </select>
                    <button
                        type="button"
                        className={acct.ok ? 'tcf-acct' : 'tcf-acct tcf-acctWarn'}
                        title={`${acct.tip} — ${t('assistant:caseForm.acctRefresh')}`}
                        onClick={() => setAcctSeq(n => n + 1)}
                    >
                        <FiUser size={10} /> {acct.label}
                    </button>
                </div>
                {!acct.ok ? <div className="tcf-hint">{t('assistant:caseForm.acctNoneHint')}</div> : null}
                <div className="tcf-row">
                    <input
                        className="tcf-input"
                        value={instruction}
                        placeholder={hasDraft ? t('assistant:caseForm.rewritePlaceholder') : t('assistant:caseForm.analyzePlaceholder')}
                        onChange={(e) => setInstruction(e.target.value)}
                    />
                    <button
                        type="button" className="tcf-btn tcf-primary"
                        disabled={drafting || (!sources.length && !hasDraft)}
                        onClick={runDraft}
                    >
                        {drafting ? <FiLoader size={11} className="tcf-spin" /> : <FiCpu size={11} />}
                        {drafting ? t('assistant:caseForm.drafting')
                            : (hasDraft ? t('assistant:caseForm.rewrite') : t('assistant:caseForm.analyze'))}
                    </button>
                </div>
                {/* 실패는 이 자리에 남는다 — 토스트로 흘리면 무엇이 잘못됐는지 사라진다 */}
                <Problem error={draftError} onClose={() => setDraftError(null)} t={t} />
                {/*
                  **여러 건을 조용히 버리지 않는다.** 자료 하나에 사례가 여럿이면 찾은 것을 전부
                  보여 주고, 지금 어느 것을 채우고 있는지 밝힌다. 나머지는 저장 후 이어서 등록한다 —
                  사례마다 사람이 분류·변조를 검토해야 하므로 한 번에 N개를 열지 않는다.
                  처음 초안은 한 건만 채운다 — 초안이 **있는** 사례와 누르면 AI 를 부르는 사례를 구분해 보인다.
                */}
                {caseListView}
            </section>
            )}

            {/* 3 양식 — 모델이 채워도 **사람이 고칠 수 있어야** 한다 */}
            {step === 'edit' && (
            <section className="tcf-step">
                <div className="tcf-head">
                    <span className="tcf-no">4</span>{t('assistant:caseForm.stepEdit')}
                </div>
                {caseListView}
                {/* **분류.** 사례가 찾히려면 같은 말로 적혀 있어야 한다 — 본문 글자가 달라도
                    코드가 같으면 걸린다. 모델이 고른 것은 제안이고, 정하는 것은 사람이다. */}
                {topicCatalog.length > 0 && (
                    <div className="tcf-topics">
                        <div className="tcf-label">
                            {t('assistant:caseForm.topics')}
                            {missingAxes.length ? (
                                <span className="tcf-incomplete" title={t('assistant:caseMeta.incompleteHint', { axes: missingAxes.map(a => t(`assistant:topicAxis.${a}`)).join(', ') })}>
                                    {t('assistant:caseMeta.incomplete')}
                                </span>
                            ) : null}
                        </div>
                        {topicAxes.map(axis => (
                            <div className="tcf-topicAxis" key={axis.id}>
                                {/* 축 이름 옆에 **뜻**을 붙인다 — 증상·원인·처방의 경계가 사람마다
                                    다르면 같은 사례가 제각각 분류된다. 정의를 화면이 들고 있어야 한다. */}
                                <span
                                    className={`tcf-topicAxisName${missingAxes.includes(axis.id) ? ' tcf-axisMissing' : ''}`}
                                    title={axis.desc}
                                >
                                    {t(`assistant:topicAxis.${axis.id}`)}
                                    {axis.optional ? <i className="tcf-axisOpt">{t('assistant:caseMeta.axisOptional')}</i> : null}
                                </span>
                                {topicCatalog.filter(tp => tp.axis === axis.id).map(tp => (
                                    <button
                                        type="button" key={tp.code}
                                        className={`tcf-topic${topics.includes(tp.code) ? ' tcf-topicOn' : ''}${tp.support ? ' tcf-topicSupport' : ''}`}
                                        title={tp.desc}
                                        onClick={() => setTopics(prev => (prev.includes(tp.code)
                                            ? prev.filter(x => x !== tp.code)
                                            : [...prev, tp.code]))}
                                    >
                                        {tp.label}
                                    </button>
                                ))}
                            </div>
                        ))}
                        <div className="tcf-hint">
                            {t('assistant:caseForm.topicsHint')}
                            {onManageTopics ? (
                                <button type="button" className="tcf-linkBtn" onClick={onManageTopics}>
                                    {t('assistant:caseForm.manageTopics')}
                                </button>
                            ) : null}
                        </div>
                    </div>
                )}

                {/* 용도·근거 — 전부 선택. 용도를 정하지 않으면 내용에서 정해진다(전/후 SQL·계획이 다 있으면 AI 참조) */}
                <div className="tcf-meta">
                    <span className="tcf-metaLabel" title={t('assistant:caseMeta.usageHint')}>{t('assistant:caseMeta.usage')}</span>
                    {USAGES.map((u) => {
                        const cur = meta.usage === null ? derivedUsage(form) : meta.usage;
                        const on = cur.includes(u);
                        return (
                            <button
                                type="button" key={u} aria-pressed={on}
                                className={`tcf-topic${on ? ' tcf-topicOn' : ''}`}
                                title={t(`assistant:caseMeta.uHint.${u}`)}
                                onClick={() => setMeta(m => ({ ...m, usage: on ? cur.filter(x => x !== u) : USAGES.filter(x => x === u || cur.includes(x)) }))}
                            >
                                {t(`assistant:caseMeta.u.${u}`)}
                            </button>
                        );
                    })}
                    {meta.usage === null
                        ? <i className="tcf-axisOpt">{t('assistant:caseMeta.auto')}</i>
                        : <button type="button" className="tcf-linkBtn" onClick={() => setMeta(m => ({ ...m, usage: null }))}>{t('assistant:caseMeta.toAuto')}</button>}
                    <span className="tcf-metaLabel tcf-metaGap">{t('assistant:caseMeta.perfBasis')}</span>
                    <select className="tcf-select tcf-metaSel" value={meta.perfBasis} aria-label={t('assistant:caseMeta.perfBasis')} onChange={e => setMeta(m => ({ ...m, perfBasis: e.target.value }))}>
                        <option value="">{t('assistant:caseMeta.unset')}</option>
                        {PERF_BASIS.map(b => <option key={b} value={b}>{t(`assistant:caseMeta.basis.${b}`)}</option>)}
                    </select>
                    <span className="tcf-metaLabel">{t('assistant:caseMeta.resultSame')}</span>
                    <select className="tcf-select tcf-metaSel" value={meta.resultSame} aria-label={t('assistant:caseMeta.resultSame')} onChange={e => setMeta(m => ({ ...m, resultSame: e.target.value }))}>
                        <option value="">{t('assistant:caseMeta.unset')}</option>
                        {RESULT_SAME.map(b => <option key={b} value={b}>{t(`assistant:caseMeta.same.${b}`)}</option>)}
                    </select>
                </div>

                {/* 쿼리·실행계획은 한 건이 수십 줄이라 같이 두면 짧은 칸이 저 아래로 밀린다 */}
                <div className="tcf-tabs">
                    {TABS.map(id => (
                        <button
                            key={id} type="button"
                            className={`tcf-tab${tab === id ? ' tcf-tabOn' : ''}`}
                            onClick={() => setTab(id)}
                        >
                            {t(`assistant:caseForm.tab.${id}`)}
                            {/* 접힌 탭의 내용은 보이지 않는다 — 있다는 것만이라도 알려 준다 */}
                            {filled[id] || (id === 'figures' && figures.length) ? <span className="tcf-dot" /> : null}
                        </button>
                    ))}
                </div>
                {tab === 'preview' ? (
                    <div className="tcf-preview">
                        <div className="tcf-hint">{t('assistant:caseForm.previewHint')}</div>
                        <ReportView markdown={caseDocument({ ...form, topics, folder, figures, visibility, status }, t, topicCatalog)} loadingText="" />
                    </div>
                ) : tab === 'figures' ? (
                    // 그림 — 파일을 고르거나, 이 칸에 붙여 넣는다(Ctrl+V — 화면 캡처를 바로)
                    <div className="tcf-figs" onPaste={(e) => { const fs = [...((e.clipboardData && e.clipboardData.files) || [])]; if (fs.length) { e.preventDefault(); addFigures(fs); } }}>
                        <div className="tcf-hint">{t('assistant:caseFig.hint')}</div>
                        <div className="tcf-row">
                            <input ref={figInputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" multiple hidden
                                data-testid="fig-file" onChange={(e) => { addFigures(e.target.files); e.target.value = ''; }} />
                            <button type="button" className="tcf-btn" disabled={figBusy || figures.length >= MAX_FIGURES} onClick={() => figInputRef.current && figInputRef.current.click()}>
                                {figBusy ? t('assistant:caseFig.uploading') : t('assistant:caseFig.add')}
                            </button>
                            <input className="tcf-input tcf-figPaste" readOnly value="" placeholder={t('assistant:caseFig.pastePh')} aria-label={t('assistant:caseFig.pastePh')} />
                            <span className="tcf-hint">{t('assistant:caseFig.count', { count: figures.length, max: MAX_FIGURES })}</span>
                        </div>
                        {/* 원본이 PPTX 면 — 슬라이드의 도식을 그림으로(도형으로 그린 것이라 그림 파일로는 꺼낼 수 없다) */}
                        {deck ? (
                            <div className="tcf-slides">
                                <div className="tcf-row">
                                    <button type="button" className="tcf-btn" aria-expanded={slidePick} onClick={openSlidePick}>
                                        {t('assistant:caseFig.slidePick', { name: deck.filename || '' })}
                                    </button>
                                    {slidePick ? (
                                        <label className="tcf-choice">
                                            <input type="checkbox" checked={slideAll} onChange={(e) => setSlideAll(e.target.checked)} />
                                            {t('assistant:caseFig.slideAll')}
                                        </label>
                                    ) : null}
                                    {slideBusy ? <span className="tcf-hint">{t('assistant:caseFig.slideBusy', { n: slideBusy })}</span> : null}
                                </div>
                                {slidePick ? (
                                    <>
                                        <div className="tcf-hint">{t('assistant:caseFig.slideHint')}</div>
                                        {!slideList || slideList.id !== deck.id ? <div className="tcf-hint">{t('common:state.loading')}</div> : (
                                            <div className="tcf-slideGrid">
                                                {slideList.slides.filter(m => slideAll || m.isDiagram).map((m) => {
                                                    const has = figures.some(f => f.slideKey === `${deck.id}:${m.no}`);
                                                    return (
                                                        <button type="button" key={m.no} className={`tcf-slide${has ? ' added' : ''}`} disabled={has || !!slideBusy || figures.length >= MAX_FIGURES}
                                                            title={m.title || ''} onClick={() => addSlideFigures(deck, [m])}>
                                                            <img loading="lazy" src={slideThumbUrl(deck.id, m.no)} alt="" />
                                                            <span className="tcf-slideCap">{t('assistant:caseFig.slideNo', { n: m.fileNo || m.no })}{has ? ` · ${t('assistant:caseFig.slideAdded')}` : ''}</span>
                                                        </button>
                                                    );
                                                })}
                                                {!slideList.slides.some(m => slideAll || m.isDiagram) ? <div className="tcf-hint">{t('assistant:caseFig.slideNone')}</div> : null}
                                            </div>
                                        )}
                                    </>
                                ) : null}
                            </div>
                        ) : null}
                        {figures.map((f, i) => (
                            <div className="tcf-fig" key={f.id}>
                                <a href={figureUrl(f.id)} target="_blank" rel="noreferrer" className={`tcf-figThumb bg-${f.bg}`}><img src={figureUrl(f.id)} alt={f.caption || f.filename || ''} /></a>
                                <div className="tcf-figBody">
                                    <input className="tcf-input" value={f.caption} maxLength={300} placeholder={t('assistant:caseFig.captionPh')}
                                        aria-label={t('assistant:caseFig.captionPh')} onChange={(e) => setFig(i, { caption: e.target.value })} />
                                    {/* 보이는 모양 — 폭(본문 폭 대비)과 배경(밝은 바탕 · 어두운 바탕 · 투명) */}
                                    <div className="tcf-row tcf-figLook">
                                        <label className="tcf-figCtl">
                                            <span className="tcf-hint">{t('assistant:caseFig.width')}</span>
                                            <input type="range" min={FIGURE_WIDTH.min} max={FIGURE_WIDTH.max} step={FIGURE_WIDTH.step} value={f.width}
                                                aria-label={t('assistant:caseFig.width')} onChange={(e) => setFig(i, { width: Number(e.target.value) })} />
                                            <span className="tcf-figPct">{f.width}%</span>
                                        </label>
                                        <label className="tcf-figCtl" title={t('assistant:caseFig.bgHint')}>
                                            <span className="tcf-hint">{t('assistant:caseFig.bg')}</span>
                                            <select className="tcf-select tcf-metaSel" value={f.bg} aria-label={t('assistant:caseFig.bg')} onChange={(e) => setFig(i, { bg: e.target.value })}>
                                                {FIGURE_BGS.map(b => <option key={b} value={b}>{t(`assistant:caseFig.bgs.${b}`)}</option>)}
                                            </select>
                                        </label>
                                    </div>
                                    <div className="tcf-row">
                                        {f.filename ? <span className="tcf-hint">{f.filename}</span> : null}
                                        {/* 공개는 범용 사례에서만 뜻이 있다 — 그림 속 이름은 기계가 검증하지 못하므로 사람이 켠다 */}
                                        {visibility === 'generic' ? (
                                            <label className="tcf-choice" title={t('assistant:caseFig.sharedHint')}>
                                                <input type="checkbox" checked={f.shared} onChange={(e) => setFig(i, { shared: e.target.checked })} />
                                                {t('assistant:caseFig.shared')}
                                            </label>
                                        ) : null}
                                        <span className="tcf-figBtns">
                                            <button type="button" className="tcf-btn" disabled={i === 0} onClick={() => moveFig(i, -1)}>{t('assistant:caseFig.up')}</button>
                                            <button type="button" className="tcf-btn" disabled={i === figures.length - 1} onClick={() => moveFig(i, 1)}>{t('assistant:caseFig.down')}</button>
                                            <button type="button" className="tcf-btn" onClick={() => setFigures(prev => prev.filter((_, j) => j !== i))}>{t('common:ui.delete')}</button>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                <div className="tcf-grid">
                    {FIELDS.filter(f => f.tab === tab).map(f => (
                        <label key={f.key} className={`tcf-field${f.half ? ' tcf-half' : ''}`}>
                            <span className="tcf-label">
                                {t(`assistant:caseForm.f.${f.key}`)}
                                {f.required ? <b className="tcf-req">*</b> : null}
                            </span>
                            {/* 글 칸(본문·유의사항 등)에는 서식 단추 — 진하게·이탤릭·색·크기(2026-10-24). 쿼리·실행계획(고정폭)에는 두지 않는다 */}
                            {f.rows > 0 && !f.mono ? (
                                <TextFormatBar targetRef={fieldRef(f.key)} value={form[f.key] || ''}
                                    onChange={(v) => setForm(prev => ({ ...prev, [f.key]: v }))} />
                            ) : null}
                            {f.rows > 0 ? (
                                <textarea
                                    ref={f.mono ? undefined : fieldRef(f.key)}
                                    className={`tcf-input${f.mono ? ' tcf-mono' : ''}`}
                                    rows={f.rows} value={form[f.key]} onChange={set(f.key)}
                                    // 실행계획은 칸에서 나갈 때 칸을 다시 맞춘다 — 손으로 고친 줄도 미리보기와 같은 모양이 된다
                                    onBlur={PLAN_KEYS.includes(f.key) ? () => setForm(prev => alignPlans(prev)) : undefined}
                                />
                            ) : (
                                <input className="tcf-input" value={form[f.key]} onChange={set(f.key)} />
                            )}
                        </label>
                    ))}
                </div>
                )}
                {/* 실행계획 표시 점검 — 어느 탭에서든 보인다(본문을 고치다 계획 쪽 표시가 빠지는 것을 바로 알게) */}
                {refIssues.length > 0 && (
                    <div className="tcf-refIssues" role="status">
                        <div className="tcf-refIssuesHead"><FiAlertCircle size={12} /> {t('assistant:caseForm.refIssues', { count: refIssues.length })}</div>
                        <ul>
                            {refIssues.map((it, i) => (
                                // eslint-disable-next-line react/no-array-index-key -- 본문 순서가 곧 키다
                                <li key={i}>
                                    <b>{t(`assistant:caseForm.refIssue.${it.kind}`, { ids: it.ids.join(', '), plan: t(`assistant:caseList.${it.side}Plan`) })}</b>
                                    <span className="tcf-refIssueText">{it.text}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="tcf-hint">
                            {t('assistant:caseForm.refIssuesHint')}
                            {tab !== 'preview' && (
                                <button type="button" className="tcf-linkBtn" onClick={() => setTab('preview')}>{t('assistant:caseForm.tab.preview')}</button>
                            )}
                        </div>
                    </div>
                )}
            </section>
            )}

            {/*
              5 대조 — **선택이다.** 사례를 쌓는 목적은 새로움이 아니라 일관성이고, 값이 있으려면
              「개선 전 내용만 보고는 도달하기 어려운 판단」이 사례에 담겨 있어야 한다. 그래서 답을 가린 채
              한 번 풀어 보고 갈린 지점을 **유의사항**으로 남긴다.

              여기서 나오는 것은 **판정이 아니라 차이**다. 한 공급자·모델의 한 번 호출로 사례의
              값어치를 매길 수 없고, 매기려는 것도 아니다. 대조를 건너뛰어도 저장된다.
            */}
            {/* 5 변조(선택) — 실제 테이블·컬럼 이름을 바꾼다. AI 는 매핑만, 바꾸기는 「양식에 적용」 이 글자 그대로.
                원본을 편집하다 적용하면 그 순간 복제 모드가 된다(원본은 그대로) */}
            {step === 'redact' && (
            <section className="tcf-step">
                <div className="tcf-head">
                    <span className="tcf-no">5</span>{t('assistant:caseForm.steps.redact')}
                    <span className="tcf-optional">{t('assistant:caseForm.optional')}</span>
                </div>
                <CaseRedactPanel
                    form={form}
                    onApplyForm={applyRename}
                    rows={redRows} setRows={setRedRows} provider={ai}
                    ruleFolder={ruleFolder} ruleId={renameRuleId} setRuleId={setRenameRuleId}
                />
            </section>
            )}
            {step === 'check' && (
            <section className="tcf-step">
                <div className="tcf-head">
                    <span className="tcf-no">6</span>{t('assistant:caseForm.stepCheck')}
                    <span className="tcf-optional">{t('assistant:caseForm.optional')}</span>
                </div>
                <div className="tcf-hint">{t('assistant:caseForm.checkHint')}</div>
                <div className="tcf-row">
                    <button
                        type="button" className="tcf-btn"
                        disabled={checking || !(form.beforeSql.trim() || form.beforePlan.trim())}
                        onClick={runCheck}
                    >
                        {checking ? <FiLoader size={11} className="tcf-spin" /> : <FiCheckCircle size={11} />}
                        {checking ? t('assistant:caseForm.checking') : t('assistant:caseForm.check')}
                    </button>
                    {!(form.beforeSql.trim() || form.beforePlan.trim())
                        ? <span className="tcf-hint">{t('assistant:caseForm.checkNeedsBefore')}</span> : null}
                </div>
                <Problem error={checkError} onClose={() => setCheckError(null)} t={t} />
                {check ? (
                    <div className="tcf-check">
                        {/* 모델이 개선 전 내용만 보고 낸 결론. 사례와 나란히 두어야 어디가 갈렸는지 보인다 */}
                        <div className="tcf-checkHead">{t('assistant:caseForm.checkVerdict')}</div>
                        {/* **무엇을 보고 낸 결론인지** — 쿼리 없이 계획만 준 경우와 구분돼야 한다(서버가 실제로 준 칸) */}
                        {check.basis ? (
                            <div className="tcf-checkLine">
                                <span className="tcf-checkAxis">{t('assistant:caseForm.checkBasis')}</span>
                                <span>{{
                                    sqlPlan: t('assistant:caseForm.checkBasisSqlPlan'),
                                    plan: t('assistant:caseForm.checkBasisPlan'),
                                    sql: t('assistant:caseForm.checkBasisSql'),
                                }[check.basis]}</span>
                            </div>
                        ) : null}
                        {['symptom', 'cause', 'fix'].map(k => (check.verdict && check.verdict[k] ? (
                            <div className="tcf-checkLine" key={k}>
                                <span className="tcf-checkAxis">{t(`assistant:topicAxis.${k}`)}</span>
                                <span>{check.verdict[k]}</span>
                            </div>
                        ) : null))}
                        {check.verdict && check.verdict.rationale ? (
                            <div className="tcf-checkLine">
                                <span className="tcf-checkAxis">{t('assistant:caseForm.checkRationale')}</span>
                                <span>{check.verdict.rationale}</span>
                            </div>
                        ) : null}
                        <ProposalCompare verdict={check.verdict} form={form} t={t} />
                        <div className="tcf-checkHead">{t('assistant:caseForm.checkDiff')}</div>
                        {(check.diff || []).map(d => (
                            <div className="tcf-checkLine" key={d.axis}>
                                <span className="tcf-checkAxis">{t(`assistant:topicAxis.${d.axis}`)}</span>
                                <span className="tcf-checkChips">
                                    {d.agreed.map(c => <span className="tcf-chipAgree" key={c}>{labelOf(c)}</span>)}
                                    {d.onlyCase.map(c => (
                                        <span className="tcf-chipCase" key={c} title={t('assistant:caseForm.onlyCase')}>
                                            {labelOf(c)}
                                        </span>
                                    ))}
                                    {d.onlyModel.map(c => (
                                        <span className="tcf-chipModel" key={c} title={t('assistant:caseForm.onlyModel')}>
                                            {labelOf(c)}
                                        </span>
                                    ))}
                                    {!d.agreed.length && !d.onlyCase.length && !d.onlyModel.length
                                        ? <span className="tcf-hint">{t('assistant:caseForm.checkNone')}</span> : null}
                                </span>
                            </div>
                        ))}
                        <div className="tcf-checkLegend">{t('assistant:caseForm.checkLegend')}</div>
                        {check.caution ? (
                            <div className="tcf-checkCaution">
                                <pre>{check.caution}</pre>
                                <button
                                    type="button" className="tcf-btn"
                                    onClick={() => setForm(prev => ({
                                        ...prev,
                                        caution: [prev.caution, check.caution].filter(x => x && x.trim()).join('\n\n'),
                                    }))}
                                >
                                    <FiPlus size={11} /> {t('assistant:caseForm.cautionApply')}
                                </button>
                            </div>
                        ) : (
                            <div className="tcf-hint">{t('assistant:caseForm.checkAgreed')}</div>
                        )}
                    </div>
                ) : null}
            </section>
            )}

            {/* 6 저장 — 여기 남는 것은 **저장 직전에 확인할 것**뿐이다.
                공개 범위는 초안을 만들기 전에 정해야 하므로 2단계로 올라갔다. */}
            {step === 'save' && (
            <section className="tcf-step">
                <div className="tcf-head">
                    <span className="tcf-no">7</span>{t('assistant:caseForm.stepSave')}
                </div>
                <div className="tcf-row">
                    <span className="tcf-visNow">
                        {t(visibility === 'generic' ? 'assistant:caseForm.visGeneric' : 'assistant:caseForm.visInternal')}
                        {visibility === 'generic' ? ` · ${t(redactionMode === 'map' ? 'assistant:caseForm.redModeMap' : 'assistant:caseForm.redModeNone')}` : ''}
                    </span>
                    <label className="tcf-choice">
                        <input type="checkbox" checked={status === 'final'} onChange={(e) => setStatus(e.target.checked ? 'final' : 'draft')} />
                        {t('assistant:caseForm.final')}
                    </label>
                </div>

                {/* 변조 매핑은 ⑤ 변조 단계 한 곳에서 고친다(공개·내부 공용) — 여기서는 몇 건인지와 가는 길만 */}
                {visibility === 'generic' && redactionMode === 'map' ? (
                    <div className="tcf-hint">
                        {t('assistant:caseForm.redaction')}: {toRows(toMap(redRows)).length}
                        {' · '}
                        <button type="button" className="tcf-linkBtn" onClick={() => setStep('redact')}>{t('assistant:caseForm.steps.redact')}</button>
                        {' — '}{t('assistant:caseForm.redHint')}
                    </div>
                ) : null}

                {/* 저장 실패 — 변조 검증에서 남은 식별자까지 그대로 보여 준다 */}
                <Problem error={saveError} onClose={() => setSaveError(null)} t={t} />

                <div className="tcf-actions">
                    <button type="button" className="tcf-btn" onClick={onCancel}>{t('common:ui.cancel')}</button>
                    <button type="button" className="tcf-btn tcf-primary" disabled={saving} onClick={save}>
                        <FiSave size={11} /> {saving ? t('assistant:caseForm.saving') : t('assistant:caseForm.save')}
                    </button>
                </div>
            </section>
            )}
            {/* 이전 / 다음 — 저장은 7단계의 저장 단추 */}
            {/* 아래 둘은 **어느 단계에서든** 보인다 — 에이전트와의 소통은 저장 단계에만 있는 것이 아니다 */}
            {/* 에이전트가 고친 양식이 도착했다 — 고치던 것을 덮지 않는다. 반영은 사용자가 고른다 */}
            {incoming ? (
                <div className="tcf-incoming" role="status">
                    <span>{t('assistant:caseForm.agentDraftArrived', { count: Object.keys(incoming.fields).length })}</span>
                    <button type="button" className="tcf-btn primary" onClick={applyIncoming}>{t('assistant:caseForm.agentDraftApply')}</button>
                    <button type="button" className="tcf-btn" onClick={() => setIncoming(null)}>{t('assistant:caseForm.agentDraftIgnore')}</button>
                </div>
            ) : null}
            {/* 이 창이 AI 패널을 가린다 — 같은 대화의 상태를 보이고, 멈추고, 지금 양식을 두고 요청할 수 있게 한다 */}
            <AgentStrip getScreen={screenForAgent} />
            <div className="tcf-stepNav">
                <button type="button" className="tcf-btn" disabled={STEPS.indexOf(step) <= 0}
                    onClick={() => setStep(STEPS[Math.max(0, STEPS.indexOf(step) - 1)])}>{t('assistant:caseForm.prev')}</button>
                {step !== 'save' && (
                    <button type="button" className="tcf-btn tcf-primary"
                        onClick={() => setStep(STEPS[Math.min(STEPS.length - 1, STEPS.indexOf(step) + 1)])}>
                        {t('assistant:caseForm.next', { step: t(`assistant:caseForm.steps.${STEPS[Math.min(STEPS.length - 1, STEPS.indexOf(step) + 1)]}`) })}
                    </button>
                )}
            </div>
        </div>
    );
}

CaseRegisterForm.propTypes = {
    /** 편집할 사례. 없으면 새 사례다. */
    // eslint-disable-next-line react/forbid-prop-types -- 저장 구조 그대로다. 여기서 다시 나열하면 양식과 두 곳으로 갈린다
    initial: PropTypes.object,
    /** 열려 있는 이 화면에 에이전트가 보낸 양식 — 덮지 않고 반영할지 묻는다 */
    agentDraft: PropTypes.object,
    /** 에이전트가 넘긴 첨부 { sources, instruction } — 첨부 단계에서 시작한다 */
    preload: PropTypes.object,
    /** 처음 열 탭(「복제 후 이름 바꾸기」 는 'redact') */
    initialTab: PropTypes.string,
    /** 초안을 만들 LLM 경로. 패널이 보여 주는 것과 같아야 과금 귀속이 갈리지 않는다. */
    provider: PropTypes.string,
    onSaved: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    /** 「유형 관리」 — 고를 것이 없으면 만들 수 있어야 한다. */
    onManageTopics: PropTypes.func,
    /** 새 사례의 기본 폴더(목록에서 고른 것) */
    defaultFolder: PropTypes.string,
    /** 저장으로 폴더가 생겼을 수 있다 — 목록의 폴더 고르기를 다시 읽게 */
    onFoldersChanged: PropTypes.func,
};

export default CaseRegisterForm;
